/**
 * NotificationBell (OCP-597) - unread count of the persisted operator
 * notifications in the Navbar. Polls `/v1/admin/notifications/unread-count`
 * every 60 s; clicking opens the /notifications inbox. Renders nothing
 * special on a failed read (the inbox page shows the error).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';

const POLL_INTERVAL_MS = 60 * 1000;

export function NotificationBell() {
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await api<{ unread: number }>('/v1/admin/notifications/unread-count');
        if (!cancelled) setUnread(r.unread);
      } catch {
        if (!cancelled) setUnread(null);
      }
    };
    void fetchOnce();
    const timer = setInterval(() => void fetchOnce(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return (
    <Link
      to="/notifications"
      aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
      data-testid="notification-bell"
      className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/60"
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
        <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 0 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {unread !== null && unread > 0 && (
        <span
          data-testid="notification-bell-count"
          className="absolute -right-0.5 -top-0.5 min-w-[1.1rem] rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-4 text-white"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
}
